import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  FormLabel,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  useTheme,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  VideoFile as VideoIcon,
  AudioFile as AudioIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from "../../../../context";
import { useFetchData } from "../../../../api/api/FetchData";
import AddExpenseTypeDialog from "./AddExpenseTypeDialog";
import { useTranslation } from "react-i18next";
import useAdd from "../../../../api/useAdd";
import { useNavigate, useLocation } from "react-router-dom";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useEffect, useState } from "react";

const Expenses = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const auth = useAuth();
  const user = auth?.user;
  const token = user?.token;
  const { expenseTypeData } = useFetchData();
  const [openExpenseTypeDialog, setOpenExpenseTypeDialog] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getCurrentTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const { data: expenseSourceData, error: fetchError } = useFetchObjects(
    ["expense-sources"],
    "expense-sources",
    token
  );

  useEffect(() => {
    if (fetchError) {
      setError(t("finance.fetchError"));
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [fetchError, t]);

  // State to control if Expense Type select should be disabled
  const [isExpenseTypeDisabled, setIsExpenseTypeDisabled] = useState(false);

  const [formData, setFormData] = useState({
    amount: "",
    expenseType: "",
    expenseSource: "",
    description: "",
    date: new Date(),
    time: getCurrentTime(),
    companyName: "",
    invoiceNumber: "",
    files: [],
  });
  const [errors, setErrors] = useState({});

  // On mount or pathname change, set expenseType and disabled state
  useEffect(() => {
    if (pathname === "/reception/add-expense") {
      setFormData((prev) => ({ ...prev, expenseType: "18" }));
      setIsExpenseTypeDisabled(true);
    } else {
      setIsExpenseTypeDisabled(false);
      setFormData((prev) => ({ ...prev, expenseType: "" }));
    }
  }, [pathname]);

  const handleInputChange = (event) => {
    const { name, value, files } = event.target;
    if (name === "files") {
      setFormData({
        ...formData,
        [name]: [...formData.files, ...Array.from(files)],
      });
    } else {
      // Prevent changing expenseType if disabled
      if (name === "expenseType" && isExpenseTypeDisabled) return;

      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleRemoveFile = (index) => {
    const newFiles = [...formData.files];
    newFiles.splice(index, 1);
    setFormData({
      ...formData,
      files: newFiles,
    });
  };

  const handleDateChange = (date) => {
    setFormData({ ...formData, date });
  };

  const getFileIcon = (file) => {
    if (!file.type) return <InsertDriveFileIcon />;

    const type = file.type.split("/")[0];
    const extension = file.name?.split(".").pop()?.toLowerCase();

    if (type === "image") return <ImageIcon />;
    if (type === "application") {
      if (extension === "pdf") return <PdfIcon />;
      return <DescriptionIcon />;
    }
    if (type === "video") return <VideoIcon />;
    if (type === "audio") return <AudioIcon />;
    return <InsertDriveFileIcon />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 KB";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const validateForm = () => {
    let isValid = true;
    let newErrors = {};

    if (formData.amount && formData.amount <= 0) {
      newErrors.amount = t(
        "finance.validAmountRequired",
        "Amount must be greater than zero"
      );
      isValid = false;
    }

    if (!formData.amount) {
      newErrors.amount = t("finance.amountRequired", "Amount is required");
      isValid = false;
    }
    if (!formData.expenseType) {
      newErrors.expenseType = t(
        "finance.expenseRequired",
        "Expense type is required"
      );
      isValid = false;
    }
    if (!formData.expenseSource) {
      newErrors.expenseSource = t(
        "finance.expenseSourceRequired",
        "Expense source is required"
      );
      isValid = false;
    }
    if (!formData.date) {
      newErrors.date = t(
        "finance.expenseDateRequired",
        "Expense date is required"
      );
      isValid = false;
    }
    if (!formData.time) {
      newErrors.time = t(
        "finance.expenseTimeRequired",
        "Expense time is required"
      );
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const {
    handleAdd,
    resetForm,
    loading: uploading,
  } = useAdd(
    "expenses",
    token,
    t("finance.addSuccess", "Expense added successfully"),
    t("finance.failedAdd", "Failed to add expense")
  );

  useEffect(() => {
    if (resetForm) {
      navigate(-1);
    }
  }, [resetForm, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const data = new FormData();
      data.append("amount", formData.amount);
      data.append("expenseType", formData.expenseType);
      data.append("expenseSource", formData.expenseSource);
      data.append("date", formData.date.toISOString().split("T")[0]);
      data.append("time", formData.time);
      data.append("description", formData.description);
      data.append("company_name", formData.companyName);
      data.append("invoice_number", formData.invoiceNumber);

      formData.files.forEach((file) => {
        data.append("files", file);
      });

      handleAdd(data);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" color="error.main" p={4}>
        <Typography>{error}</Typography>
      </Box>
    );
  }

  return (
    <Grid container maxWidth="xl" mt={2} justifyContent="center" mb={10}>
      <Grid item xs={12} lg={11.8} component="form" onSubmit={handleSubmit}>
        <Paper
          sx={{
            p: 4,
            borderRadius: 2,
            boxShadow: theme.shadows[2],
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Grid item xs={12} md={10} margin="0 auto">
            <Grid
              container
              rowGap={2}
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" mb={4}>
                  <Tooltip title={t("finance.goBack", "Go Back")} arrow>
                    <IconButton
                      onClick={() => navigate(-1)}
                      aria-label={t("finance.goBack", "Go Back")}
                      sx={{ mr: 2, color: theme.palette.text.primary }}
                    >
                      <ArrowBackIcon fontSize="large" />
                    </IconButton>
                  </Tooltip>
                  <Typography variant="h4" fontWeight={600}>
                    {t("finance.expenses", "Expenses")}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={3.5}>
                <FormLabel sx={{ mb: 1, display: "block", fontWeight: 500 }}>
                  {t("finance.expenseAmount", "Expense Amount")}
                </FormLabel>
                <TextField
                  fullWidth
                  type="number"
                  name="amount"
                  size="small"
                  value={formData.amount}
                  error={!!errors.amount}
                  helperText={errors.amount}
                  onChange={handleInputChange}
                  sx={{ borderRadius: 1 }}
                  InputProps={{ sx: { borderRadius: 1 } }}
                />
              </Grid>

              <Grid item xs={12} md={3.5}>
                <FormLabel sx={{ mb: 1, display: "block", fontWeight: 500 }}>
                  {t("finance.companyName", "Company Name")}
                </FormLabel>
                <TextField
                  fullWidth
                  name="companyName"
                  size="small"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  sx={{ borderRadius: 1 }}
                  InputProps={{ sx: { borderRadius: 1 } }}
                  placeholder={t(
                    "finance.companyNamePlaceholder",
                    "Enter company name"
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3.5}>
                <FormLabel sx={{ mb: 1, display: "block", fontWeight: 500 }}>
                  {t("finance.invoiceNumber", "Invoice Number")}
                </FormLabel>
                <TextField
                  fullWidth
                  name="invoiceNumber"
                  size="small"
                  value={formData.invoiceNumber}
                  onChange={handleInputChange}
                  sx={{ borderRadius: 1 }}
                  InputProps={{ sx: { borderRadius: 1 } }}
                  placeholder={t(
                    "finance.invoiceNumberPlaceholder",
                    "Enter invoice number"
                  )}
                />
              </Grid>

              <Grid container gap={2} justifyContent="space-between" mt={2}>
                <Grid item xs={12} md={3.5}>
                  <FormControl
                    size="small"
                    fullWidth
                    error={!!errors.expenseType}
                    disabled={isExpenseTypeDisabled}
                  >
                    <InputLabel id="expense-type-label">
                      {t("finance.expenseType", "Expense Type")}
                    </InputLabel>
                    <Select
                      labelId="expense-type-label"
                      id="expense-type"
                      name="expenseType"
                      value={formData.expenseType}
                      label={t("finance.expenseType", "Expense Type")}
                      onChange={handleInputChange}
                      sx={{ borderRadius: 1 }}
                    >
                      <MenuItem value="">
                        <em>
                          {t(
                            "finance.selectExpenseType",
                            "Select Expense Type"
                          )}
                        </em>
                      </MenuItem>
                      {expenseTypeData?.map((item) => (
                        <MenuItem value={item.id} key={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.expenseType && (
                      <FormHelperText>{errors.expenseType}</FormHelperText>
                    )}
                  </FormControl>

                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setOpenExpenseTypeDialog(true)}
                    sx={{
                      display: "block",
                      mt: 1,
                      borderRadius: 1,
                      textTransform: "none",
                    }}
                    disabled={isExpenseTypeDisabled}
                  >
                    {t("finance.addNewExpenseType", "Add New Expense Type")}
                  </Button>
                </Grid>

                <Grid item xs={12} md={3.5}>
                  <FormControl
                    size="small"
                    fullWidth
                    error={!!errors.expenseSource}
                  >
                    <InputLabel id="expense-source-label">
                      {t("finance.expenseSource", "Expense Source")}
                    </InputLabel>
                    <Select
                      labelId="expense-source-label"
                      id="expense-source"
                      name="expenseSource"
                      value={formData.expenseSource}
                      label={t("finance.expenseSource", "Expense Source")}
                      onChange={handleInputChange}
                      sx={{ borderRadius: 1 }}
                    >
                      <MenuItem value="">
                        <em>
                          {t(
                            "finance.selectExpenseSource",
                            "Select Expense Source"
                          )}
                        </em>
                      </MenuItem>
                      {Array.isArray(expenseSourceData) &&
                        expenseSourceData.map((item) => (
                          <MenuItem value={item.id} key={item.id}>
                            {item.name}
                          </MenuItem>
                        ))}
                    </Select>
                    {errors.expenseSource && (
                      <FormHelperText>{errors.expenseSource}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3.5}>
                  <FormControl fullWidth size="small" error={!!errors.date}>
                    <DatePicker
                      selected={formData.date}
                      onChange={handleDateChange}
                      dateFormat="yyyy-MM-dd"
                      className="form-control date-picker-input"
                      customInput={
                        <TextField
                          fullWidth
                          size="small"
                          error={!!errors.date}
                          helperText={errors.date}
                          sx={{ borderRadius: 1 }}
                          InputProps={{ sx: { borderRadius: 1 } }}
                        />
                      }
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3.5}>
                  <FormControl fullWidth size="small" error={!!errors.time}>
                    <TextField
                      fullWidth
                      type="time"
                      name="time"
                      size="small"
                      label={t("finance.expenseTime", "Expense Time")}
                      value={formData.time}
                      error={!!errors.time}
                      helperText={errors.time}
                      onChange={handleInputChange}
                      InputLabelProps={{ shrink: true }}
                      sx={{ borderRadius: 1 }}
                      InputProps={{ sx: { borderRadius: 1 } }}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <Grid item xs={12} mt={3}>
                <FormLabel sx={{ mb: 1, display: "block", fontWeight: 500 }}>
                  {t("finance.description", "Description")}
                </FormLabel>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  name="description"
                  size="small"
                  value={formData.description}
                  onChange={handleInputChange}
                  sx={{ borderRadius: 1 }}
                  InputProps={{ sx: { borderRadius: 1 } }}
                />
              </Grid>

              <Grid item xs={12} mt={3}>
                <FormLabel sx={{ mb: 1, display: "block", fontWeight: 500 }}>
                  {t("finance.uploadFile", "Upload File")}
                </FormLabel>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<CloudUploadIcon />}
                  sx={{
                    py: 2,
                    mb: 2,
                    borderRadius: 1,
                    borderStyle: "dashed",
                    textTransform: "none",
                    "&:hover": {
                      borderStyle: "dashed",
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  {t("finance.clickToUpload", "Click To Upload")}
                  <input
                    type="file"
                    name="files"
                    hidden
                    multiple
                    onChange={handleInputChange}
                  />
                </Button>

                {formData.files.length > 0 && (
                  <Box
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 500 }}
                    >
                      {t("finance.selectedFiles", "Selected Files")} (
                      {formData.files.length})
                    </Typography>
                    <List dense disablePadding>
                      {formData.files.map((file, index) => (
                        <ListItem
                          key={index}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleRemoveFile(index)}
                              sx={{ color: "error.main" }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          }
                          sx={{
                            px: 2,
                            py: 1,
                            mb: 1,
                            borderRadius: 1,
                            bgcolor: "background.default",
                            "&:hover": {
                              bgcolor: "action.hover",
                            },
                          }}
                        >
                          <ListItemAvatar sx={{ minWidth: 40 }}>
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: "grey.200",
                                color: "text.secondary",
                              }}
                            >
                              {getFileIcon(file)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{
                                  fontWeight: 500,
                                  maxWidth: 300,
                                }}
                              >
                                {file.name}
                              </Typography>
                            }
                            secondary={
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {formatFileSize(file.size)}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Grid>

              <Grid container mt={4} justifyContent="flex-end">
                <Button
                  variant="contained"
                  type="submit"
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 1,
                    textTransform: "none",
                    fontWeight: 600,
                    backgroundColor: "#071952",
                    "&:hover": {
                      backgroundColor: "#0a2463",
                    },
                  }}
                  disabled={uploading}
                >
                  {uploading ? "Saving..." : t("finance.saveNew", "Save New")}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <AddExpenseTypeDialog
        open={openExpenseTypeDialog}
        onClose={() => setOpenExpenseTypeDialog(false)}
        token={token}
      />
    </Grid>
  );
};

export default Expenses;
